import { Card, Colors, FOCUS_RING, Icon, Pills, Text, Typography } from '@amelio/ui-library';
import { useId } from 'react';

import { RadioMarker } from './RadioMarker';

import type { RovingRadioProps } from '@/hooks/useRovingRadioGroup';
import type { MeetingTemplate, ThematicMeetingTopic } from '@/types/thematic-meetings';

import { useGetMeetingTemplates } from '@/api/thematic-meetings';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useRovingRadioGroup } from '@/hooks/useRovingRadioGroup';
import { meetingTemplateDurationMinutes } from '@/pages/ThematicMeetings/utils/meetingTemplateDuration';
import { getTextByLanguage } from '@/utils/localization';

/**
 * How a plan is identified on this screen, and the value the created meeting is given: its own id. A
 * company may hold several plans for one topic, so the topic chosen on the screen before narrows the
 * list and this says which of them the meeting will follow.
 */
export type MeetingTemplateChoice = string;

interface TemplateStepProps {
    /** The topic chosen on the screen before: the plans offered here are the ones written for it. */
    topic: ThematicMeetingTopic;
    selectedTemplate: MeetingTemplateChoice | null;
    onSelectTemplate: (template: MeetingTemplateChoice) => void;
}

interface TemplateTileProps {
    template: MeetingTemplate;
    language: string;
    radioProps: RovingRadioProps;
}

/**
 * The row the marker stands in, so the circle sits on the first line of the plan's name instead of
 * in the middle of the whole tile. 24px is the line box of that name's `text-md`, which is also the
 * geometry the library gives the `md` radio this marker borrows its look from.
 */
const MARKER_LINE = 'h-[24px]';

const TemplateTile = ({ template, language, radioProps }: TemplateTileProps) => {
    const { t } = useAmelioTranslations();
    const objectiveId = useId();
    const isSelected = radioProps['aria-checked'];
    const title = getTextByLanguage(template.title, language);
    const objective = getTextByLanguage(template.objective, language);
    const durationMinutes = meetingTemplateDurationMinutes(template);

    return (
        <Card
            padding={'none'}
            borderRadius={'2xl'}
            borderWidth={'default'}
            borderColor={isSelected ? Colors.PRIMARY : Colors.BASE_100}
            bgColor={isSelected ? 'bg-primary-50' : 'bg-white'}
        >
            <div
                {...radioProps}
                aria-label={title}
                aria-describedby={objectiveId}
                data-testid={`meeting-template-tile-${template.id}`}
                // The radio carries the padding and the hover, so the whole tile answers the pointer
                // and the focus ring follows the tile's edge.
                className={`${FOCUS_RING} hover:bg-primary-50 flex cursor-pointer gap-[12px] rounded-2xl p-[16px] transition-colors`}
            >
                <div
                    aria-hidden={'true'}
                    className={`${MARKER_LINE} flex shrink-0 items-center`}
                >
                    <RadioMarker
                        isSelected={isSelected}
                        dataTestId={`meeting-template-tile-${template.id}-indicator`}
                    />
                </div>

                <div className={'flex min-w-0 flex-1 flex-col gap-[4px]'}>
                    {/* The duration ends the name's own line: stacked plans then line their
                        durations up in one column, which is how the manager compares them. */}
                    <div className={'flex items-center gap-[16px]'}>
                        <Text
                            weight={'bold'}
                            className={'flex-1'}
                        >
                            {title}
                        </Text>

                        {/* How long a meeting held from this plan runs, read from the plan's own
                            agenda. A plan with no agenda has no answer to give, and a pill reading
                            "0 minutes" would be a wrong one, so it says nothing instead. */}
                        {durationMinutes > 0 && (
                            <Pills
                                variant={'primary'}
                                size={'sm'}
                                className={'shrink-0'}
                                dataTestId={`meeting-template-tile-${template.id}-duration`}
                            >
                                {/* The colour is spelled out because `Icon` paints itself
                                    `Colors.BASE` unless told otherwise, and a filled pill is not
                                    one of the containers that hand it their own text colour — so
                                    the clock came out navy beside white wording. Every other
                                    filled pill in the module names the colour here too. */}
                                <Icon
                                    name={'time-clock'}
                                    size={'sm'}
                                    color={Colors.WHITE}
                                    ariaHidden
                                />

                                {t('meetingAgenda.minutes', { count: durationMinutes })}
                            </Pills>
                        )}
                    </div>

                    <Typography
                        id={objectiveId}
                        variant={'caption'}
                        color={Colors.BASE_400}
                    >
                        {objective}
                    </Typography>
                </div>
            </div>
        </Card>
    );
};

/**
 * Second screen of the flow's first step: which plan the meeting will follow. The plans are the
 * company's own — every company is given the catalogue's nine, may reword them and may write others
 * — and the ones offered here are those written for the topic just chosen.
 *
 * The tiles form a radiogroup and follow the WAI-ARIA pattern: a single tab stop, arrow keys that
 * move focus and selection, Space and Enter to choose the focused tile. A plan is required to move
 * on, because the server builds the meeting's agenda from it and refuses to create a meeting whose
 * topic has none.
 */
export const TemplateStep = ({ topic, selectedTemplate, onSelectTemplate }: TemplateStepProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { data: templates, isLoading, isError } = useGetMeetingTemplates();

    const topicTemplates = (templates ?? []).filter((template) => template.topic === topic);
    const { getRadioProps } = useRovingRadioGroup({
        options: topicTemplates.map((template) => template.id),
        selected: selectedTemplate,
        onSelect: onSelectTemplate,
    });

    if (isError) {
        return <EmptyState message={t('thematicMeetings.newMeeting.templatesError')} />;
    }

    // Nothing to choose from is not "no plans": while the catalogue is on its way the loader stands
    // in for it, and the empty message is only ever the answer once it has arrived.
    if (!isLoading && topicTemplates.length === 0) {
        return <EmptyState message={t('thematicMeetings.newMeeting.templatesEmpty')} />;
    }

    return (
        <OverlayLoader isLoading={isLoading}>
            <div
                role={'radiogroup'}
                aria-label={t('thematicMeetings.newMeeting.templatesGroupLabel')}
                className={'flex flex-col gap-[16px]'}
                data-testid={'meeting-template-tiles'}
            >
                {topicTemplates.map((template) => (
                    <TemplateTile
                        key={template.id}
                        template={template}
                        language={language}
                        radioProps={getRadioProps(template.id)}
                    />
                ))}
            </div>

            {!selectedTemplate && (
                <Text
                    size={'small'}
                    color={Colors.BASE_500}
                    className={'mt-[16px]'}
                >
                    {t('thematicMeetings.newMeeting.templateStepRequirement')}
                </Text>
            )}
        </OverlayLoader>
    );
};
